import { isClientView } from '../common/organizationAccessMode';
import { formatLocalDateTime } from '../utils/localDateTime';
import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseConfig';
import type { EpSurveyTask } from '../../functions/src/common/epSurveyTasks';

const callable = httpsCallable(functions, 'manageServiceRequests');

export default function EpSurveyClientTasks({ organizationId, requestId }: { organizationId: string; requestId: string }) {
    const [tasks, setTasks] = useState<EpSurveyTask[]>([]);
    const [busy, setBusy] = useState('loading');
    const [error, setError] = useState('');
    useEffect(() => {
        let active = true;
        callable({ action: 'surveyClientTasks', organizationId, requestId }).then(response => {
            if (active) {
                setTasks((response.data as any).tasks || []);
                setBusy('');
            }
        }).catch(failure => {
            if (active) {
                setError(failure.message || 'Client checks failed loading.');
                setBusy('');
            }
        });
        return () => { active = false; };
    }, [organizationId, requestId]);
    const update = (id: string, changes: Partial<EpSurveyTask>) => setTasks(current =>
        current.map(task => task.id === id ? { ...task, ...changes } : task));
    const save = async (task: EpSurveyTask) => {
        setBusy(task.id);
        setError('');
        try {
            const response = await callable({ action: 'surveyClientTasks', organizationId, requestId,
                taskId: task.id, status: task.status, note: task.note });
            const saved = ((response.data as any).tasks || []).find((item: EpSurveyTask) => item.id === task.id);
            if (saved)
                update(task.id, saved);
        } catch (failure: any) {
            setError(failure.message || 'Client check failed saving.');
        } finally {
            setBusy('');
        }
    };
    return <section className="mt-5 space-y-4 border-t border-slate-200 pt-5">
        <h3 className="text-lg font-bold text-slate-900">Advance team ground checks</h3>
        <p className="text-sm text-slate-600">Record physical verification here. Unfinished checks stay visible in the report.</p>
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        {busy === 'loading' && <p role="status" className="text-sm text-slate-600">Loading checks…</p>}
        {tasks.map(task => <div key={task.id} className="rounded-lg border border-slate-200 p-4">
            <p className="font-semibold text-slate-800">{task.title}</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-[12rem_1fr_auto] sm:items-end">
                <label className="text-sm font-semibold">Status<select disabled={isClientView} value={task.status} onChange={event => update(task.id, { status: event.target.value as EpSurveyTask['status'] })}
                    className="mt-1 w-full rounded-lg border border-slate-300 bg-white p-2 font-normal">
                    <option value="open">Open</option><option value="in_progress">In progress</option>
                    <option value="verified">Verified</option><option value="blocked">Blocked</option>
                </select></label>
                <label className="text-sm font-semibold">Evidence or limitation<textarea readOnly={isClientView} rows={2} maxLength={1000} value={task.note}
                    onChange={event => update(task.id, { note: event.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-300 p-2 font-normal" /></label>
                <button type="button" disabled={isClientView || !!busy || task.status === 'verified' && !task.note.trim()}
                    onClick={() => void save(task)} className="rounded-lg bg-blue-700 px-4 py-2 font-semibold text-white disabled:opacity-40">
                    {busy === task.id ? 'Saving…' : 'Save check'}</button>
            </div>
            {task.updatedAt && <p className="mt-2 text-xs text-slate-500">Saved {formatLocalDateTime(task.updatedAt)}</p>}
        </div>)}
    </section>;
}
